import "./styles.css";
import { CombatAudio } from "./audio";
import { KeyboardInput } from "./input";
import { MultiplayerClient, type ConnectionStatus, type LeaderboardResponse } from "./network";
import { SubmarineRenderer } from "./renderer";
import { PracticeSession, practiceInstructions } from "./practice";
import { SCORE_LIMIT } from "../shared/constants";
import type { ChatMessage, GameEvent, GameSnapshot, MatchResult, MatchmakeResponse, PlayerProfile, PlayerState, RoomConfig, RoomPublicState, ServerMessage, Vector3State } from "../shared/types";
import { buildSonarContacts, clampInteger, distanceMeters, escapeHtml, formatTime, loadStoredProfile, normalizeRoomCode, roomJoinBlocked, sanitizeName, type ClientSnapshot, type SonarContact, type PickupState, type PickupKind } from "./helpers";

type ViewState = "menu" | "invite" | "practice" | "matchmaking" | "connecting" | "lobby" | "playing" | "finished";
type EventSource = "regular" | "homing" | "mine" | "pickup";


type ClientEvent = Omit<GameEvent, "type"> & {
  type: GameEvent["type"] | "pickup_collected";
  position?: Vector3State;
  source?: EventSource;
};


const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("Missing #app root");

app.innerHTML = `
  <div class="shell">
    <div class="canvas-host" data-canvas></div>
    <div class="ui-layer" data-ui></div>
    <div class="game-actions" data-actions hidden>
      <button class="button primary" data-ready>Ready Up</button>
      <button class="button" data-loadout>Loadout</button>
      <button class="button primary" data-reconnect>Reconnect</button>
      <button class="button primary" data-rematch>Rematch</button>
      <button class="button" data-copy>Invite Again</button>
      <button class="button" data-menu>Leave</button>
    </div>
    <div class="session-chat" data-comms hidden>
      <div class="feed chat-feed" data-chat-history aria-live="polite"></div>
      <div class="chat-box"><input data-chat maxlength="120" placeholder="Squad comms" aria-label="Squad comms" /><button class="button" data-send-chat>Send</button></div>
    </div>
  </div>
`;

const canvasHost = app.querySelector<HTMLDivElement>("[data-canvas]")!;
const uiRoot = app.querySelector<HTMLDivElement>("[data-ui]")!;
if (!canvasHost || !uiRoot) throw new Error("App shell failed to initialize");

const gameActions = app.querySelector<HTMLDivElement>("[data-actions]")!;
const comms = app.querySelector<HTMLDivElement>("[data-comms]")!;
const chatInput = comms.querySelector<HTMLInputElement>("[data-chat]")!;
const chatHistory = comms.querySelector<HTMLDivElement>("[data-chat-history]")!;
const sendChat = () => {
  if (!playerId || connectionStatus !== "open" || !chatInput.value.trim()) return;
  network.chat(chatInput.value);
  chatInput.value = "";
};
comms.querySelector("[data-send-chat]")!.addEventListener("click", sendChat);
chatInput.addEventListener("keydown", (event) => {
  if (event.key === "Enter" && !event.isComposing) {
    event.preventDefault();
    sendChat();
  }
});

const renderer = new SubmarineRenderer(canvasHost);
const input = new KeyboardInput();
const audio = new CombatAudio();
const seenEvents = new Set<string>();
const eventFeed: string[] = [];
const inviteParam = new URLSearchParams(window.location.search).get("room");
const initialRoomFromUrl = inviteParam?.trim().toUpperCase() ?? "";
let pendingInvite: string | null = inviteParam === null ? null : /^[A-Z0-9-]{1,16}$/.test(initialRoomFromUrl) ? initialRoomFromUrl : "";

let view: ViewState = pendingInvite === null ? "menu" : "invite";
let roomId = pendingInvite ?? normalizeRoomCode(localStorage.getItem("submarine:lastRoom") ?? "");
let playerName = localStorage.getItem("submarine:name") || "Captain";
let profile: PlayerProfile | null = loadStoredProfile();
let playerId: string | null = null;
let playerToken: string | null = null;
let snapshot: ClientSnapshot | null = null;
let publicState: RoomPublicState | null = null;
let requestedRoomState: RoomPublicState | null = null;
let matchResult: MatchResult | null = null;
let leaderboard: LeaderboardResponse | null = null;
const chatFeed: ChatMessage[] = [];
let connectionStatus: ConnectionStatus = "idle";
let connectionDetail = "";
let pingMs = 0;
let jitterMs = 0;
let lastPingMs = 0;
let lastReconciledSeq = 0;
let lastInputSentAt = 0;
let centerMessage = "";
let centerMessageUntil = 0;
let flashUntil = 0;
let matchTicketId: string | null = null;
let matchExpiresAt = 0;
let matchPollTimer: number | null = null;
let matchSearchStartedAt = 0;
let postMatchSyncKey: string | null = null;
let privateMode: RoomConfig["mode"] = "deathmatch";
let privateMaxPlayers = 4;
let privateScoreLimit = SCORE_LIMIT;
let privateDurationMinutes = 5;
let connectionAttempt = 0;
let practice: PracticeSession | null = null;
let practicePaused = false;
let lastPracticeTick = 0;

window.addEventListener("blur", pausePractice);
window.addEventListener("popstate", () => {
  if (pendingInvite === null) return;
  if (view === "practice") exitPractice();
  else if (view === "connecting") {
    connectionAttempt++;
    network.disconnect(false);
    view = "invite";
    renderUI();
  }
});
document.addEventListener("visibilitychange", () => {
  if (document.hidden) pausePractice();
});

const network = new MultiplayerClient({
  onMessage: handleServerMessage,
  onStatus(status, detail) {
    connectionStatus = status;
    connectionDetail = detail ?? "";
    if (status === "connecting") view = "connecting";
    if (status === "error" && view === "connecting") view = connectionRecoveryView();
    renderUI();
  },
  onPing(latencyMs) {
    jitterMs = lastPingMs ? jitterMs * 0.75 + Math.abs(latencyMs - lastPingMs) * 0.25 : 0;
    lastPingMs = latencyMs;
    pingMs = latencyMs;
    renderUI();
  }
});

bindLobby();
bindNavigation(gameActions);
bindMatchButtons(gameActions);
renderUI();
void refreshLeaderboard();
void autoJoinRequestedRoom();
requestAnimationFrame(frame);
window.setInterval(() => {
  if (view === "lobby" && getLobbyAutoStartAt() !== null) renderUI();
  if (view === "matchmaking") renderUI();
}, 500);

function frame(now: number): void {
  if (view === "practice" && practice && !practicePaused && now - lastPracticeTick >= 50) {
    practice.tick(input.sample());
    renderer.setSnapshot(practice.snapshot(), practice.captain.id);
    updatePracticeUI();
    lastPracticeTick = now;
  }
  if (input.consumeViewToggle()) {
    const firstPerson = renderer.toggleFirstPerson();
    showCenterMessage(firstPerson ? "First-person view" : "Chase view", 1200);
    renderUI();
  }

  if (snapshot?.matchState === "playing" && playerId && connectionStatus === "open" && now - lastInputSentAt >= 50) {
    const sampled = input.sample();
    const local = getLocalPlayer();
    if (sampled.homing && (local?.ammo.homing ?? 0) <= 0) showCenterMessage("No homing torpedoes", 1100);
    if (sampled.mine && (local?.ammo.mines ?? 0) <= 0) showCenterMessage("No mines ready", 1100);
    network.input(sampled);
    lastReconciledSeq = Math.max(lastReconciledSeq, sampled.seq - 1);
    lastInputSentAt = now;
  }
  renderer.render();
  requestAnimationFrame(frame);
}

function handleServerMessage(message: ServerMessage): void {
  if (message.type === "welcome") {
    roomId = message.roomId;
    pendingInvite = null;
    setRoomInUrl(roomId);
    playerId = message.playerId;
    playerToken = message.playerToken;
    postMatchSyncKey = null;
    const nextSnapshot = ensureClientSnapshot(message.snapshot);
    snapshot = nextSnapshot;
    matchResult = nextSnapshot.matchResult;
    input.resumeAfter(nextSnapshot.lastInputSeqByPlayer[playerId] ?? 0);
    publicState = null;
    requestedRoomState = null;
    connectionDetail = "";
    localStorage.setItem(tokenKey(roomId), playerToken);
    localStorage.setItem("submarine:lastRoom", roomId);
    renderer.setSnapshot(nextSnapshot, playerId);
    view = nextSnapshot.matchState === "playing" ? "playing" : nextSnapshot.matchState === "finished" ? "finished" : "lobby";
    nextSnapshot.events.forEach(handleEvent);
    renderUI();
    return;
  }

  if (message.type === "lobby") {
    publicState = message.state;
    mergeChat(message.state.chat);
    if (snapshot) snapshot = { ...snapshot, rematchRequestedBy: message.state.rematchRequestedBy };
    if (message.state.phase === "lobby") {
      view = "lobby";
      matchResult = null;
    }
    renderUI();
    return;
  }

  if (message.type === "snapshot") {
    const nextSnapshot = ensureClientSnapshot(message.snapshot);
    snapshot = nextSnapshot;
    renderer.setSnapshot(nextSnapshot, playerId);
    view = nextSnapshot.matchState;
    matchResult = nextSnapshot.matchResult;
    if (nextSnapshot.matchResult) {
      matchResult = nextSnapshot.matchResult;
      void syncPostMatchStats(nextSnapshot.matchResult.matchId);
    }
    nextSnapshot.events.forEach(handleEvent);
    renderUI();
    return;
  }

  if (message.type === "event") {
    handleEvent(message.event);
    renderUI();
    return;
  }

  if (message.type === "reconciled") {
    if (message.playerId === playerId) lastReconciledSeq = message.lastInputSeq;
    return;
  }

  if (message.type === "match_result") {
    if (snapshot?.matchState !== "finished" || snapshot.matchResult?.matchId !== message.result.matchId) return;
    matchResult = message.result;
    view = "finished";
    void syncPostMatchStats(message.result.matchId);
    renderUI();
    return;
  }

  if (message.type === "chat") {
    mergeChat([message.chat]);
    renderUI();
    return;
  }

  if (message.type === "room_config") {
    if (snapshot) snapshot = { ...snapshot, config: message.config, scoreLimit: message.config.scoreLimit, maxPlayers: message.config.maxPlayers };
    renderUI();
    return;
  }

  if (message.type === "spectator_snapshot") {
    snapshot = ensureClientSnapshot(message.snapshot);
    requestedRoomState = null;
    playerId = null;
    playerToken = null;
    renderer.setSnapshot(snapshot, null);
    view = snapshot.matchState;
    matchResult = snapshot.matchResult;
    renderUI();
    return;
  }

  if (message.type === "error") {
    if (view === "connecting") {
      network.disconnect(false);
      view = connectionRecoveryView();
    }
    connectionDetail = message.message;
    renderUI();
  }
}

function handleEvent(event: ClientEvent): void {
  if (seenEvents.has(event.id)) return;
  seenEvents.add(event.id);
  eventFeed.unshift(event.message);
  eventFeed.splice(6);
  audio.playEvent(event);

  if (event.position && (event.type === "player_damaged" || event.type === "player_sunk" || event.type === "pickup_collected")) {
    renderer.spawnImpact(event.position, event.source);
  }

  if (event.type === "pickup_collected") {
    showCenterMessage(event.message || "Pickup collected", 1300);
  }

  if (event.targetId === playerId && event.type === "player_damaged") {
    renderer.pulseLocalDamage();
    flashUntil = performance.now() + 420;
    showCenterMessage(event.value ? `Hull breach -${Math.round(event.value)}` : "Hull breach", 1100);
  }

  if (event.targetId === playerId && event.type === "player_sunk") {
    renderer.pulseLocalDamage();
    flashUntil = performance.now() + 650;
    showCenterMessage("You were sunk", 1800);
  }
}

function mergeChat(messages: ChatMessage[]): void {
  const byId = new Map(chatFeed.map((chat) => [chat.id, chat]));
  for (const chat of messages) byId.set(chat.id, chat);
  chatFeed.splice(0, chatFeed.length, ...[...byId.values()].sort((a, b) => b.at - a.at).slice(0, 6));
}

function renderUI(): void {
  if (view === "practice") {
    gameActions.hidden = true;
    comms.hidden = true;
    return;
  }
  gameActions.hidden = view !== "lobby" && view !== "playing" && view !== "finished";
  const lobbyPlayers = publicState?.players ?? snapshot?.players ?? [];
  const localReady = lobbyPlayers.find((player) => player.id === playerId)?.ready ?? false;
  const readyButton = gameActions.querySelector<HTMLButtonElement>("[data-ready]")!;
  readyButton.hidden = view !== "lobby" || !playerId || (publicState?.roomKind ?? snapshot?.roomKind) === "auto";
  readyButton.disabled = connectionStatus !== "open" || lobbyPlayers.filter((player) => player.connected).length < 2;
  const readyLabel = localReady ? "Stand Down" : "Ready Up";
  if (readyButton.textContent !== readyLabel) readyButton.textContent = readyLabel;
  gameActions.querySelector<HTMLButtonElement>("[data-loadout]")!.hidden = view !== "lobby" || !playerId;
  gameActions.querySelector<HTMLButtonElement>("[data-reconnect]")!.hidden = connectionStatus === "open";
  gameActions.querySelector<HTMLButtonElement>("[data-rematch]")!.hidden = view !== "finished" || !playerId;
  gameActions.querySelector<HTMLButtonElement>("[data-rematch]")!.disabled = connectionStatus !== "open";
  const copyButton = gameActions.querySelector<HTMLButtonElement>("[data-copy]")!;
  copyButton.hidden = (view !== "finished" && view !== "lobby") || (view === "lobby" && (publicState?.roomKind ?? snapshot?.roomKind) === "auto");
  const copyLabel = view === "lobby" ? "Copy Invite" : "Invite Again";
  if (copyButton.textContent !== copyLabel) copyButton.textContent = copyLabel;
  const leaveButton = gameActions.querySelector<HTMLButtonElement>("[data-menu]")!;
  const leaveLabel = view === "finished" ? "Return to Menu" : "Leave";
  if (leaveButton.textContent !== leaveLabel) leaveButton.textContent = leaveLabel;
  comms.hidden = !snapshot || view === "menu" || view === "matchmaking";
  comms.querySelector<HTMLDivElement>(".chat-box")!.hidden = !playerId;
  comms.dataset.view = view;
  const historyHtml = chatFeed.map((chat) => `<div><strong>${escapeHtml(chat.playerName)}:</strong> ${escapeHtml(chat.message)}</div>`).join("");
  if (chatHistory.innerHTML !== historyHtml) chatHistory.innerHTML = historyHtml;
  comms.querySelector<HTMLButtonElement>("[data-send-chat]")!.disabled = connectionStatus !== "open";
  if (view === "menu") {
    uiRoot.innerHTML = renderMenu();
    bindMenu();
    return;
  }

  if (view === "invite") {
    uiRoot.innerHTML = renderInvite();
    bindInvite();
    return;
  }

  if (view === "matchmaking") {
    uiRoot.innerHTML = renderMatchmaking();
    bindMatchmaking();
    return;
  }

  if (view === "connecting") {
    uiRoot.innerHTML = renderConnecting();
    bindCommon();
    return;
  }

  if (view === "lobby") {
    uiRoot.innerHTML = renderLobby();
    bindCommon();
    return;
  }

  uiRoot.innerHTML = renderGameHud();
  bindCommon();
}

function renderInvite(): string {
  return `
    <section class="panel menu" aria-label="Room invitation">
      <h1 class="brand">${pendingInvite ? `Join room ${escapeHtml(pendingInvite)}` : "Invalid invite"}</h1>
      <p class="subtle">${pendingInvite ? "Choose your captain name and join your friends. Practice is optional." : "This invite has no valid room code. Ask your friend for a new link."}</p>
      <label class="field">Captain name
        <input data-name maxlength="18" value="${escapeHtml(playerName)}" autocomplete="off" />
      </label>
      <p class="subtle">Online combat requires a keyboard: W/A/D to move, F/S for depth, Space to fire.</p>
      <div class="play-actions">
        <button class="button primary" data-join-invite ${pendingInvite ? "" : "disabled"}>Join room</button>
        <button class="button" data-practice>Practise first (optional)</button>
        <button class="button" data-menu>Return to menu</button>
      </div>
      ${connectionDetail ? `<p class="danger" role="alert">${escapeHtml(connectionDetail)}</p>` : ""}
    </section>`;
}

function saveInviteName(): void {
  playerName = sanitizeName(uiRoot.querySelector<HTMLInputElement>("[data-name]")?.value ?? playerName);
  localStorage.setItem("submarine:name", playerName);
}

function bindInvite(): void {
  const join = () => {
    if (!pendingInvite) return;
    saveInviteName();
    void audio.start();
    void connectToRoom(pendingInvite);
  };
  uiRoot.querySelector("[data-join-invite]")!.addEventListener("click", join);
  uiRoot.querySelector<HTMLInputElement>("[data-name]")!.addEventListener("input", saveInviteName);
  uiRoot.querySelector<HTMLInputElement>("[data-name]")!.addEventListener("keydown", event => {
    if (event.key === "Enter" && !event.isComposing) join();
  });
  uiRoot.querySelector("[data-practice]")!.addEventListener("click", () => {
    saveInviteName();
    startPractice();
  });
  bindNavigation(uiRoot);
}

function renderMenu(): string {
  const savedToken = roomId ? localStorage.getItem(tokenKey(roomId)) : null;
  const joinBlocked = requestedRoomState ? roomJoinBlocked(requestedRoomState, savedToken) : false;
  const requestedRoom = roomId ? renderRoomStatusCard(requestedRoomState, savedToken) : "";
  const profileLine = profile
    ? `<div class="profile-strip">
        <strong>${escapeHtml(profile.displayName)}</strong>
        <span>${profile.stats.wins} wins</span>
        <span>${profile.stats.sinks} sinks</span>
        <span>${profile.stats.rating} rating</span>
      </div>`
    : "";
  return `
    <section class="panel menu menu-home">
      <h1 class="brand">Friend Arena</h1>
      <p class="subtle">A submarine battle with friends. Create a room and share the invite link.</p>
      ${profileLine}
      <label class="field">
        Captain name
        <input data-name maxlength="18" value="${escapeHtml(playerName)}" autocomplete="off" />
      </label>
      <div class="play-actions">
        <button class="button primary" data-create>Play with friends</button>
        <button class="button" data-practice>Practise the basics · about 1 minute</button>
        <button class="button" data-quick>Quick Match</button>
        <a class="button classic-link" href="/classic/working.html">Classic Solo</a>
      </div>
      <details class="private-match" open>
        <summary>Friend room setup</summary>
        <div class="setup-grid">
          <label class="field">
            Mode
            <select data-mode>
              <option value="deathmatch" ${privateMode === "deathmatch" ? "selected" : ""}>Free-for-all</option>
              <option value="team_deathmatch" ${privateMode === "team_deathmatch" ? "selected" : ""}>Team Deathmatch</option>
            </select>
          </label>
          <label class="field">
            Captains
            <select data-max-players>
              ${[2, 3, 4].map((count) => `<option value="${count}" ${privateMaxPlayers === count ? "selected" : ""}>${count}</option>`).join("")}
            </select>
          </label>
          <label class="field">
            Score limit
            <input data-score-limit type="number" min="1" max="50" value="${privateScoreLimit}" />
          </label>
          <label class="field">
            Round minutes
            <input data-duration-minutes type="number" min="1" max="20" value="${privateDurationMinutes}" />
          </label>
        </div>
      </details>
      <details class="private-match" ${roomId ? "open" : ""}>
        <summary>Join invite</summary>
        <label class="field">
          Room code
          <input data-room maxlength="16" value="${escapeHtml(roomId)}" autocomplete="off" />
        </label>
        ${requestedRoom}
        <div class="actions">
          <button class="button" data-auto-join ${roomId && !joinBlocked ? "" : "disabled"}>${joinBlocked ? "Join Closed" : savedToken ? "Reconnect Room" : "Join Room"}</button>
          <button class="button" data-spectate ${roomId ? "" : "disabled"}>Spectate</button>
          <button class="button" data-copy ${roomId ? "" : "disabled"}>Copy Invite</button>
        </div>
      </details>
      ${renderLeaderboardPreview()}
      ${connectionDetail ? `<p class="danger">${escapeHtml(connectionDetail)}</p>` : ""}
    </section>
  `;
}

function renderRoomStatusCard(state: RoomPublicState | null, savedToken: string | null): string {
  if (!roomId) return "";
  if (!state) {
    return `<div class="invite-card">
      <strong>Room ${escapeHtml(roomId)}</strong>
      <span>${savedToken ? "Reconnect token found on this device." : "Ready to check this invite."}</span>
    </div>`;
  }
  const connected = state.players.filter((player) => player.connected).length;
  const status = state.phase === "lobby" ? `${connected}/${state.maxPlayers} captains` : state.phase === "playing" ? "Match in progress" : "Round complete";
  const mode = state.config.mode === "team_deathmatch" ? "Team Deathmatch" : "Free-for-all";
  return `<div class="invite-card room-status">
    <strong>Room ${escapeHtml(state.roomId)} <span class="status-pill">${escapeHtml(status)}</span></strong>
    <span>${mode} &middot; ${state.config.scoreLimit} sinks &middot; ${Math.round(state.config.durationMs / 60000)} min &middot; ${state.spectatorCount ?? 0} spectators</span>
    <span>${savedToken ? "Reconnect will use your saved slot." : roomJoinBlocked(state, savedToken) ? "Join is closed; spectating is available." : "Join is open."}</span>
  </div>`;
}

function renderMatchmaking(): string {
  const expiresIn = matchExpiresAt ? Math.max(0, matchExpiresAt - Date.now()) : 0;
  const elapsed = matchSearchStartedAt ? Math.max(0, Date.now() - matchSearchStartedAt) : 0;
  return `
    <section class="panel menu">
      <h1 class="brand">Quick Match</h1>
      <p class="subtle">Searching for one captain, then launching a 10-second combat countdown.</p>
      <div class="matchmaking-pulse"></div>
      <div class="queue-stats">
        <span>Elapsed ${Math.ceil(elapsed / 1000)}s</span>
        ${expiresIn ? `<span>Ticket ${Math.ceil(expiresIn / 1000)}s</span>` : ""}
      </div>
      <div class="actions">
        <button class="button" data-cancel-match>Cancel</button>
      </div>
      ${connectionDetail ? `<p class="danger">${escapeHtml(connectionDetail)}</p>` : ""}
    </section>
  `;
}

function renderLeaderboardPreview(): string {
  const players = leaderboard?.players.slice(0, 5) ?? [];
  return `
    <div class="leaderboard-preview">
      <div class="leaderboard-heading">
        <span>Leaderboard</span>
        <button class="button small" data-refresh-leaderboard>Refresh</button>
      </div>
      ${
        players.length
          ? players
              .map(
                (entry, index) => `
                  <div class="leaderboard-row">
                    <span>${index + 1}. ${escapeHtml(entry.displayName)}</span>
                    <strong>${entry.stats.rating}</strong>
                  </div>
                `
              )
              .join("")
          : `<div class="sonar-empty">Play a match to seed the board.</div>`
      }
    </div>
  `;
}

function renderConnecting(): string {
  return `
    <section class="panel menu">
      <h1 class="brand">Connecting</h1>
      <p class="subtle">Opening a secure channel to room ${escapeHtml(roomId)}.</p>
      <div class="actions">
        <button class="button" data-menu>Cancel</button>
      </div>
      ${connectionDetail ? `<p class="danger">${escapeHtml(connectionDetail)}</p>` : ""}
    </section>
  `;
}

function renderLobby(): string {
  const state = publicState;
  const players = state?.players ?? snapshot?.players ?? [];
  const roomKind = state?.roomKind ?? snapshot?.roomKind ?? "private";
  const config = state?.config ?? snapshot?.config;
  const isAutoRoom = roomKind === "auto";
  const hostPlayerId = state?.hostPlayerId ?? snapshot?.hostPlayerId ?? null;
  const spectatorCount = state?.spectatorCount ?? snapshot?.spectatorCount ?? 0;
  const connectedCount = players.filter((player) => player.connected).length;
  const maxPlayers = state?.maxPlayers ?? snapshot?.maxPlayers ?? 4;
  const canReady = connectedCount >= 2;
  const autoStartAt = getLobbyAutoStartAt();
  const countdownMs = autoStartAt ? Math.max(0, autoStartAt - Date.now()) : 0;
  const waitingFor = Math.max(0, maxPlayers - connectedCount);
  return `
    <section class="panel menu">
      <h1 class="brand">${isAutoRoom ? "Quick Match" : `Room ${escapeHtml(roomId)}`}</h1>
      ${config ? `<p class="subtle">${config.mode === "team_deathmatch" ? "Team Deathmatch" : "Free-for-all"} &middot; ${config.scoreLimit} sinks &middot; ${Math.round(config.durationMs / 60000)} min &middot; ${spectatorCount} spectators</p>` : ""}
      ${!isAutoRoom ? `<div class="invite-card compact"><strong>Invite link</strong><span>${escapeHtml(inviteUrlForRoom(roomId))}</span></div>` : ""}
      <p class="subtle">${
        isAutoRoom
          ? autoStartAt
            ? `Combat starts in ${Math.ceil(countdownMs / 1000)}s.`
            : `Waiting for ${waitingFor} matched captain${waitingFor === 1 ? "" : "s"}.`
          : "Deathmatch starts when all connected captains are ready."
      }</p>
      <div class="lobby-list">
        ${players
          .map(
            (player) => `
              <div class="lobby-player">
                <span>${teamBadge(player.teamId)}${escapeHtml(player.name)}${player.id === hostPlayerId ? " &middot; Host" : ""}${player.id === playerId ? " (you)" : ""}</span>
                ${playerLobbyStatus(player, isAutoRoom)}
              </div>
            `
          )
          .join("")}
      </div>
      <p class="subtle">Ping ${Math.round(pingMs)}ms &middot; jitter ${Math.round(jitterMs)}ms &middot; ${escapeHtml(connectionStatus)}</p>
      ${!isAutoRoom && !canReady ? `<p class="subtle">Waiting for one more player.</p>` : ""}
      ${connectionDetail ? `<p class="danger">${escapeHtml(connectionDetail)}</p>` : ""}
    </section>
  `;
}

function renderGameHud(): string {
  const snap = snapshot;
  const local = snap?.players.find((player) => player.id === playerId);
  const players = [...(snap?.players ?? [])].sort((a, b) => b.score - a.score);
  const pickups = snap?.pickups ?? [];
  const health = Math.max(0, Math.round(local?.health ?? 0));
  const healthPct = Math.max(0, Math.min(100, health));
  const timeLeft = snap?.endsAt ? Math.max(0, snap.endsAt - snap.serverTime) : 0;
  const finished = snap?.matchState === "finished";
  const respawn = local && !local.alive && local.respawnAt ? Math.max(0, local.respawnAt - snap!.serverTime) : 0;
  const depth = local ? Math.max(0, Math.round(-local.position.y)) : 0;
  const altitude = local ? Math.max(0, Math.round(local.position.y + 82)) : 0;
  const sonarContacts = local && snap ? buildSonarContacts(snap, local.id) : [];
  const activeCenterMessage = centerMessage && performance.now() < centerMessageUntil ? centerMessage : "";
  const flashing = performance.now() < flashUntil;

  return `
    <div class="flash-overlay ${flashing ? "active" : ""}"></div>
    ${activeCenterMessage ? `<div class="center-message">${escapeHtml(activeCenterMessage)}</div>` : ""}
    <div class="top-left">
      <div class="hud">
        <div class="hud-title">${finished ? "Round Complete" : "Combat Status"}</div>
        <div class="stat-row"><span>HP</span><strong>${health}/100</strong></div>
        <div class="bar"><span style="width:${healthPct}%"></span></div>
        <div class="stat-row"><span>Homing</span><strong>${local?.ammo.homing ?? 0}</strong></div>
        <div class="stat-row"><span>Mines</span><strong>${local?.ammo.mines ?? 0}</strong></div>
        <div class="stat-row"><span>Pickups</span><strong>${pickups.length}</strong></div>
        <div class="stat-row"><span>Ack</span><strong>${lastReconciledSeq}</strong></div>
      </div>
      <div class="depth-gauge">
        <div class="hud-title">Depth</div>
        <div class="depth-track"><span style="height:${Math.min(100, depth)}%"></span></div>
        <div class="depth-readout">${depth}m below surface</div>
        <div class="depth-readout muted">Clearance ${altitude}m</div>
      </div>
      <div class="chip">W/A/D move &middot; F/S depth &middot; Space fire &middot; H homing &middot; M mine &middot; P view</div>
    </div>
    <div class="top-right">
      <div class="scoreboard">
        <div class="hud-title">Score ${snap?.scoreLimit ?? SCORE_LIMIT} to win</div>
        ${players
          .map(
            (player) => `
              <div class="score-row ${player.id === playerId ? "local" : ""} ${player.connected ? "" : "disconnected"}">
                <span>${teamBadge(player.teamId)}${escapeHtml(player.name)}${player.connected ? "" : " (reconnecting)"}</span>
                <strong>${player.score}</strong>
              </div>
            `
          )
          .join("")}
      </div>
      ${snap ? renderRoomDrawer(snap, players) : ""}
      <div class="sonar">
        <div class="hud-title">Sonar</div>
        ${
          sonarContacts.length
            ? sonarContacts.map((contact) => `<div class="sonar-row ${contact.kind}"><span>${escapeHtml(contact.label)}</span><strong>${contact.distance}m</strong></div>`).join("")
            : `<div class="sonar-empty">No contacts</div>`
        }
      </div>
    </div>
    <div class="bottom-left">
      ${eventFeed.length ? `<div class="feed">${eventFeed.map((event) => `<div>${escapeHtml(event)}</div>`).join("")}</div>` : ""}

    </div>
    <div class="bottom-right" style="bottom: ${finished ? 220 : connectionStatus !== "open" ? 115 : 65}px">
      ${finished && matchResult ? renderMatchResult(matchResult) : ""}

      ${respawn > 0 ? `<div class="chip danger">Respawn in ${Math.ceil(respawn / 1000)}s</div>` : ""}
    </div>
  `;
}

function startPractice(): void {
  connectionAttempt++;
  if (pendingInvite !== null && view !== "practice") {
    const url = new URL(window.location.href);
    url.searchParams.set("practice", "1");
    window.history.pushState({}, "", url);
  }
  input.reset();
  renderer.setFirstPerson(false);
  practice = new PracticeSession();
  practicePaused = false;
  lastPracticeTick = performance.now();
  view = "practice";
  renderer.setSnapshot(practice.snapshot(), practice.captain.id);
  renderUI();
  uiRoot.innerHTML = `
    <section class="panel practice-guide" aria-label="Practice guide">
      <div class="hud-title">Local practice · about 1 minute · no timer</div>
      <h1 data-practice-title></h1>
      <p data-practice-hint></p>
      <p data-practice-progress></p>
      <p class="ready" data-practice-feedback role="status"></p>
      <div class="practice-readouts">
        <div class="depth-gauge" data-practice-depth></div>
        <div class="sonar" data-practice-sonar></div>
      </div>
      <button class="button primary" data-confirm-contact hidden>I see Practice target on sonar</button>
      <button class="button" data-reset-lane hidden>Reset firing lane</button>
      <button class="button primary" data-resume-practice hidden>Resume practice</button>
      <div class="actions">
        <button class="button" data-retry-practice>Retry practice</button>
        <button class="button" data-exit-practice>${pendingInvite !== null ? "Back to invite" : "Exit"}</button>
        <button class="button primary" data-practice-friends ${pendingInvite === "" ? "disabled" : ""}>${pendingInvite !== null ? "Join invited room" : "Play with friends"}</button>
      </div>
    </section>
    <div class="practice-controls" aria-label="Practice controls">
      ${[["KeyW", "Forward"], ["KeyA", "Left"], ["KeyD", "Right"], ["KeyS", "Dive"], ["KeyF", "Rise"], ["Space", "Fire"]].map(([code, label]) => `<button class="button" data-control="${code}">${label}</button>`).join("")}
    </div>`;
  uiRoot.querySelector("[data-retry-practice]")!.addEventListener("click", startPractice);
  uiRoot.querySelector("[data-exit-practice]")!.addEventListener("click", exitPractice);
  uiRoot.querySelector("[data-practice-friends]")!.addEventListener("click", () => {
    exitPractice();
    if (pendingInvite) void connectToRoom(pendingInvite);
    else if (pendingInvite === null) void createPrivateRoom();
  });
  uiRoot.querySelector("[data-confirm-contact]")!.addEventListener("click", () => {
    input.reset();
    practice?.confirmContact();
    updatePracticeUI();
  });
  uiRoot.querySelector("[data-reset-lane]")!.addEventListener("click", () => {
    input.reset();
    practice?.resetLane();
  });
  uiRoot.querySelector("[data-resume-practice]")!.addEventListener("click", () => {
    input.reset();
    practicePaused = false;
    lastPracticeTick = performance.now();
    updatePracticeUI();
  });
  for (const button of uiRoot.querySelectorAll<HTMLButtonElement>("[data-control]")) {
    const code = button.dataset.control!;
    button.addEventListener("pointerdown", event => {
      event.preventDefault();
      if (practicePaused || practice?.step === "complete") return;
      button.setPointerCapture(event.pointerId);
      input.setControl(code, true);
    });
    for (const event of ["pointerup", "pointercancel", "lostpointercapture"]) {
      button.addEventListener(event, () => input.setControl(code, false));
    }
  }
  updatePracticeUI();
}

function pausePractice(): void {
  if (view !== "practice") return;
  input.reset();
  practicePaused = true;
  updatePracticeUI();
}

function exitPractice(): void {
  if (practice) renderer.setSnapshot({ ...practice.snapshot(), matchState: "lobby", players: [], projectiles: [], mines: [], pickups: [] }, null);
  practice = null;
  input.reset();
  const url = new URL(window.location.href);
  url.searchParams.delete("practice");
  window.history.replaceState({}, "", url);
  view = pendingInvite !== null ? "invite" : "menu";
  renderUI();
}

function updatePracticeUI(): void {
  if (!practice || view !== "practice") return;
  const instruction = practiceInstructions[practice.step];
  const setText = (selector: string, text: string) => {
    const element = uiRoot.querySelector(selector)!;
    if (element.textContent !== text) element.textContent = text;
  };
  setText("[data-practice-title]", practicePaused ? "Practice paused" : instruction.title);
  const hint = practice.step === "complete" && pendingInvite !== null
    ? "Your invitation is saved. Join your friends when you're ready. Online combat requires a keyboard."
    : instruction.hint;
  setText("[data-practice-hint]", practicePaused ? "Your controls have been released. Resume when you're ready." : hint);
  setText("[data-practice-feedback]", practice.feedback);
  setText("[data-practice-progress]", practice.progress());
  setText("[data-practice-depth]", `Depth: ${Math.round(-practice.captain.position.y)}m below surface`);
  const contact = buildSonarContacts(practice.snapshot(), practice.captain.id).find(contact => contact.label === "Practice target");
  setText("[data-practice-sonar]", `Sonar · Practice target: ${contact?.distance ?? "?"}m`);
  uiRoot.querySelector<HTMLButtonElement>("[data-confirm-contact]")!.hidden = practicePaused || practice.step !== "sonar";
  uiRoot.querySelector<HTMLButtonElement>("[data-reset-lane]")!.hidden = practicePaused || practice.step !== "fire";
  uiRoot.querySelector<HTMLButtonElement>("[data-resume-practice]")!.hidden = !practicePaused;
  uiRoot.querySelector<HTMLElement>(".practice-controls")!.hidden = practicePaused || practice.step === "complete";
}

function bindMenu(): void {
  uiRoot.querySelector<HTMLButtonElement>("[data-practice]")?.addEventListener("click", () => {
    playerName = sanitizeName(uiRoot.querySelector<HTMLInputElement>("[data-name]")?.value ?? playerName);
    startPractice();
  });
  const nameInput = uiRoot.querySelector<HTMLInputElement>("[data-name]");
  const roomInput = uiRoot.querySelector<HTMLInputElement>("[data-room]");
  const autoJoinButton = uiRoot.querySelector<HTMLButtonElement>("[data-auto-join]");
  const spectateButton = uiRoot.querySelector<HTMLButtonElement>("[data-spectate]");
  const copyButton = uiRoot.querySelector<HTMLButtonElement>("[data-copy]");

  roomInput?.addEventListener("input", () => {
    const hasRoomCode = Boolean(normalizeRoomCode(roomInput.value));
    if (autoJoinButton) autoJoinButton.disabled = !hasRoomCode;
    if (spectateButton) spectateButton.disabled = !hasRoomCode;
    if (copyButton) copyButton.disabled = !hasRoomCode;
  });

  uiRoot.querySelector<HTMLButtonElement>("[data-quick]")?.addEventListener("click", async () => {
    await audio.start();
    playerName = sanitizeName(nameInput?.value ?? playerName);
    localStorage.setItem("submarine:name", playerName);
    requestedRoomState = null;
    await ensureProfile();
    startQuickMatch();
  });

  autoJoinButton?.addEventListener("click", async () => {
    await audio.start();
    playerName = sanitizeName(nameInput?.value ?? playerName);
    roomId = normalizeRoomCode(roomInput?.value ?? roomId);
    localStorage.setItem("submarine:name", playerName);
    await connectToRoom(roomId);
  });

  uiRoot.querySelector<HTMLButtonElement>("[data-refresh-leaderboard]")?.addEventListener("click", () => {
    void refreshLeaderboard(true);
  });

  uiRoot.querySelector<HTMLButtonElement>("[data-create]")?.addEventListener("click", async () => {
    await createPrivateRoom();
  });

  spectateButton?.addEventListener("click", async () => {
    await audio.start();
    playerName = sanitizeName(nameInput?.value ?? playerName);
    roomId = normalizeRoomCode(roomInput?.value ?? roomId);
    localStorage.setItem("submarine:name", playerName);
    setRoomInUrl(roomId);
    localStorage.setItem("submarine:lastRoom", roomId);
    view = "connecting";
    renderUI();
    network.connect(roomId, playerName, undefined, profile?.id ?? undefined, true);
  });

  copyButton?.addEventListener("click", () => {
    const code = normalizeRoomCode(roomInput?.value ?? roomId);
    if (!code) return;
    roomId = code;
    void navigator.clipboard?.writeText(inviteUrlForRoom(roomId));
    showCenterMessage("Invite link copied", 1200);
  });
}

async function createPrivateRoom(): Promise<void> {
  const attempt = ++connectionAttempt;
  await audio.start();
  if (attempt !== connectionAttempt) return;
  const nameInput = uiRoot.querySelector<HTMLInputElement>("[data-name]");
  playerName = sanitizeName(nameInput?.value ?? playerName);
  localStorage.setItem("submarine:name", playerName);
  readPrivateRoomSettings();
  await ensureProfile();
  if (attempt !== connectionAttempt) return;
  try {
    const created = await network.createRoom(playerName, profile?.id, {
      mode: privateMode,
      maxPlayers: privateMaxPlayers,
      scoreLimit: privateScoreLimit,
      durationMs: privateDurationMinutes * 60 * 1000,
      mapSeed: "classic-trench",
      friendlyFire: false
    });
    if (attempt !== connectionAttempt) return;
    roomId = created.roomId;
    setRoomInUrl(roomId);
    playerToken = created.playerToken;
    localStorage.setItem(tokenKey(roomId), playerToken);
    localStorage.setItem("submarine:lastRoom", roomId);
    requestedRoomState = null;
    network.connect(roomId, playerName, playerToken, profile?.id ?? undefined);
  } catch (error) {
    if (attempt !== connectionAttempt) return;
    connectionDetail = error instanceof Error ? error.message : "Could not create room";
    renderUI();
  }
}

function readPrivateRoomSettings(): void {
  const mode = (uiRoot.querySelector("[data-mode]") as HTMLSelectElement | null)?.value;
  const maxPlayers = Number((uiRoot.querySelector("[data-max-players]") as HTMLSelectElement | null)?.value);
  const scoreLimit = Number(uiRoot.querySelector<HTMLInputElement>("[data-score-limit]")?.value);
  const durationMinutes = Number(uiRoot.querySelector<HTMLInputElement>("[data-duration-minutes]")?.value);

  privateMode = mode === "team_deathmatch" ? "team_deathmatch" : "deathmatch";
  privateMaxPlayers = clampInteger(maxPlayers, 2, 4, 4);
  privateScoreLimit = clampInteger(scoreLimit, 1, 50, SCORE_LIMIT);
  privateDurationMinutes = clampInteger(durationMinutes, 1, 20, 5);
}

function bindMatchmaking(): void {
  uiRoot.querySelector<HTMLButtonElement>("[data-cancel-match]")?.addEventListener("click", () => {
    cancelQuickMatch();
  });
}

async function startQuickMatch(): Promise<void> {
  stopMatchPolling();
  matchTicketId = null;
  matchExpiresAt = 0;
  matchSearchStartedAt = Date.now();
  postMatchSyncKey = null;
  connectionDetail = "";
  view = "matchmaking";
  renderUI();

  try {
    await ensureProfile();
    handleMatchmakeResponse(await network.requestMatch(playerName, profile?.id));
  } catch (error) {
    connectionDetail = error instanceof Error ? error.message : "Could not start matchmaking";
    view = "menu";
    renderUI();
  }
}

function handleMatchmakeResponse(response: MatchmakeResponse): void {
  if (response.status === "matched") {
    stopMatchPolling();
    matchTicketId = null;
    matchExpiresAt = 0;
    matchSearchStartedAt = 0;
    roomId = response.roomId;
    setRoomInUrl(roomId);
    playerToken = response.playerToken;
    localStorage.setItem(tokenKey(roomId), playerToken);
    network.connect(roomId, playerName, playerToken, profile?.id ?? undefined);
    return;
  }

  matchTicketId = response.ticketId;
  matchExpiresAt = response.expiresAt;
  view = "matchmaking";
  scheduleMatchPoll(response.pollAfterMs);
  renderUI();
}

function scheduleMatchPoll(delayMs: number): void {
  stopMatchPolling();
  matchPollTimer = window.setTimeout(async () => {
    if (!matchTicketId || view !== "matchmaking") return;
    try {
      handleMatchmakeResponse(await network.pollMatch(matchTicketId));
    } catch (error) {
      stopMatchPolling();
      matchTicketId = null;
      matchExpiresAt = 0;
      matchSearchStartedAt = 0;
      postMatchSyncKey = null;
      connectionDetail = error instanceof Error && error.message.includes("410") ? "Matchmaking timed out." : "Could not complete matchmaking.";
      view = "menu";
      renderUI();
    }
  }, Math.max(250, delayMs));
}

function stopMatchPolling(): void {
  if (matchPollTimer !== null) {
    window.clearTimeout(matchPollTimer);
    matchPollTimer = null;
  }
}

async function cancelQuickMatch(): Promise<void> {
  const ticketId = matchTicketId;
  stopMatchPolling();
  matchTicketId = null;
  matchExpiresAt = 0;
  matchSearchStartedAt = 0;
  connectionDetail = "";
  view = "menu";
  renderUI();
  if (ticketId) {
    try {
      await network.cancelMatch(ticketId);
    } catch {
      connectionDetail = "Could not cancel matchmaking cleanly.";
      renderUI();
    }
  }
}

async function autoJoinRequestedRoom(): Promise<void> {
  if (!pendingInvite || !localStorage.getItem(tokenKey(pendingInvite))) return;
  await connectToRoom(roomId, true);
}

function connectionRecoveryView(): ViewState {
  if (pendingInvite !== null) return "invite";
  // Keep an admitted captain's controls available after a failed reconnect.
  // A snapshot from another room must never become the recovery destination.
  return snapshot?.roomId === roomId ? snapshot.matchState : "menu";
}

async function connectToRoom(code: string, silent = false): Promise<void> {
  const attempt = ++connectionAttempt;
  const cleanCode = normalizeRoomCode(code);
  if (!cleanCode) {
    connectionDetail = "Enter a room code.";
    renderUI();
    return;
  }

  roomId = cleanCode;
  setRoomInUrl(roomId);
  playerToken = localStorage.getItem(tokenKey(roomId));
  localStorage.setItem("submarine:lastRoom", roomId);
  connectionDetail = "";
  view = "connecting";
  renderUI();
  const state = await lookupRoomState(roomId);
  if (attempt !== connectionAttempt) return;
  if (!state) {
    connectionDetail = "This room is unavailable. Check the invite with your friend or try again.";
    view = connectionRecoveryView();
    renderUI();
    return;
  }
  if (roomJoinBlocked(state, playerToken)) {
    requestedRoomState = state;
    publicState = state;
    connectionDetail = state.phase === "lobby" ? "Room is full. Ask your friend to free a place, then try again." : "This round has already started or finished. Ask your friend to return to the lobby, then try again.";
    view = connectionRecoveryView();
    renderUI();
    return;
  }

  requestedRoomState = state;
  if (!silent) connectionDetail = playerToken ? "Reconnecting with your saved room token." : "Joining invite room.";
  view = "connecting";
  postMatchSyncKey = null;
  renderUI();
  await ensureProfile();
  if (attempt !== connectionAttempt) return;
  // Preserve the admitted identity if the first welcome is interrupted.
  playerToken ??= crypto.randomUUID();
  localStorage.setItem(tokenKey(roomId), playerToken);
  network.connect(roomId, playerName, playerToken, profile?.id ?? undefined);
}

async function lookupRoomState(code: string): Promise<RoomPublicState | null> {
  try {
    const state = await network.getRoom(code);
    requestedRoomState = state;
    return state;
  } catch {
    requestedRoomState = null;
    return null;
  }
}



async function refreshLeaderboard(forceRender = false): Promise<void> {
  try {
    leaderboard = await network.getLeaderboard("all");
    if (forceRender || view === "menu") renderUI();
  } catch {
    if (forceRender) {
      connectionDetail = "Leaderboard unavailable.";
      renderUI();
    }
  }
}

async function syncPostMatchStats(matchId: string): Promise<void> {
  if (postMatchSyncKey === matchId) return;
  postMatchSyncKey = matchId;
  await ensureProfile();
  await refreshLeaderboard(view === "finished");
}

function bindLobby(): void {
  gameActions.querySelector<HTMLButtonElement>("[data-ready]")?.addEventListener("click", () => {
    const players = publicState?.players ?? snapshot?.players ?? [];
    const local = players.find((player) => player.id === playerId);
    network.ready(!local?.ready);
  });
}

function bindCommon(): void {
  bindMatchButtons(uiRoot);
  bindNavigation(uiRoot);
}

function bindNavigation(root: HTMLElement): void {
  root.querySelectorAll<HTMLButtonElement>("[data-reconnect]").forEach((button) => {
    button.addEventListener("click", () => {
      void connectToRoom(roomId);
    });
  });
  root.querySelectorAll<HTMLButtonElement>("[data-copy]").forEach((button) => {
    button.addEventListener("click", () => {
      if (!roomId) return;
      void navigator.clipboard?.writeText(inviteUrlForRoom(roomId));
      showCenterMessage("Invite link copied", 1200);
    });
  });
  root.querySelectorAll<HTMLButtonElement>("[data-menu]").forEach((button) => {
    button.addEventListener("click", () => {
      connectionAttempt++;
      if (matchTicketId) void network.cancelMatch(matchTicketId);
      stopMatchPolling();
      matchTicketId = null;
      matchExpiresAt = 0;
      matchSearchStartedAt = 0;
      network.disconnect();
      playerId = null;
      playerToken = null;
      matchResult = null;
      chatFeed.length = 0;
      chatInput.value = "";
      eventFeed.length = 0;
      seenEvents.clear();
      snapshot = null;
      publicState = null;
      connectionDetail = "";
      view = pendingInvite !== null && view === "connecting" ? "invite" : "menu";
      if (view === "menu") {
        pendingInvite = null;
        clearRoomInUrl();
      }
      renderer.setSnapshot(
        {
          tick: 0,
          serverTime: Date.now(),
          roomId,
          roomKind: "private",
          config: {
            mode: "deathmatch",
            maxPlayers: 4,
            scoreLimit: SCORE_LIMIT,
            durationMs: 5 * 60 * 1000,
            mapSeed: "classic-trench",
            friendlyFire: false
          },
          matchState: "lobby",
          players: [],
          projectiles: [],
          mines: [],
          pickups: [],
          events: [],
          autoStartAt: null,
          endsAt: null,
          lastInputSeqByPlayer: {},
          matchResult: null,
          scoreLimit: SCORE_LIMIT,
          maxPlayers: 4
        },
        null
      );
      renderUI();
    });
  });
}

function bindMatchButtons(root: HTMLElement): void {
  root.querySelector<HTMLButtonElement>("[data-rematch]")?.addEventListener("click", () => {
    network.requestRematch();
    showCenterMessage("Rematch requested", 1200);
  });
  root.querySelector<HTMLButtonElement>("[data-loadout]")?.addEventListener("click", () => {
    const hull = profile?.loadout.hull === "assault" ? "scout" : profile?.loadout.hull === "scout" ? "support" : "assault";
    const loadout = { hull, skin: hull === "scout" ? "gold" : "classic" } as const;
    if (profile) {
      profile = { ...profile, loadout };
      storeProfile(profile);
    }
    network.setLoadout(loadout);
    showCenterMessage(`${hull} loadout`, 1200);
  });
}

function ensureClientSnapshot(next: GameSnapshot): ClientSnapshot {
  return { ...next, pickups: (next as ClientSnapshot).pickups ?? [], spectatorCount: next.spectatorCount ?? 0, rematchRequestedBy: next.rematchRequestedBy ?? [] };
}

function getLocalPlayer(): PlayerState | undefined {
  return snapshot?.players.find((player) => player.id === playerId);
}

function getLobbyAutoStartAt(): number | null {
  return publicState?.autoStartAt ?? snapshot?.autoStartAt ?? null;
}

async function ensureProfile(): Promise<PlayerProfile | null> {
  try {
    profile = await network.createProfileSession(playerName, profile?.id ?? localStorage.getItem("submarine:profileId"));
    storeProfile(profile);
    return profile;
  } catch (error) {
    connectionDetail = error instanceof Error ? error.message : "Profile session unavailable";
    return profile;
  }
}



function storeProfile(next: PlayerProfile): void {
  localStorage.setItem("submarine:profileId", next.id);
  localStorage.setItem("submarine:profile", JSON.stringify(next));
}

function teamBadge(teamId: PlayerState["teamId"]): string {
  if (teamId === "blue") return `<span class="team blue">Blue</span> `;
  if (teamId === "gold") return `<span class="team gold">Gold</span> `;
  return "";
}

function playerLobbyStatus(player: PlayerState, isAutoRoom: boolean): string {
  if (player.connected) {
    const label = isAutoRoom ? "Connected" : player.ready ? "Ready" : "Waiting";
    return `<span class="${player.ready || isAutoRoom ? "ready" : ""}">${label}</span>`;
  }
  const graceMs = player.reconnectGraceUntil ? Math.max(0, player.reconnectGraceUntil - Date.now()) : 0;
  const label = graceMs > 0 ? `Rejoin ${Math.ceil(graceMs / 1000)}s` : "Disconnected";
  return `<span class="danger">${label}</span>`;
}

function renderRoomDrawer(snap: ClientSnapshot, players: PlayerState[]): string {
  const config = snap.config;
  const connected = players.filter((player) => player.connected).length;
  const mode = config.mode === "team_deathmatch" ? "Teams" : "FFA";
  const timeLeft = snap.endsAt ? Math.max(0, snap.endsAt - snap.serverTime) : 0;
  return `
    <div class="room-drawer">
      <div class="hud-title">Room ${escapeHtml(snap.roomId)}</div>
      <div class="stat-row"><span>Mode</span><strong>${mode}</strong></div>
      <div class="stat-row"><span>Captains</span><strong>${connected}/${snap.maxPlayers}</strong></div>
      <div class="stat-row"><span>Spectators</span><strong>${snap.spectatorCount ?? 0}</strong></div>
      <div class="stat-row"><span>Timer</span><strong>${formatTime(timeLeft)}</strong></div>
      <div class="stat-row"><span>Ping</span><strong>${Math.round(pingMs)}ms</strong></div>
      <div class="stat-row"><span>Jitter</span><strong>${Math.round(jitterMs)}ms</strong></div>
    </div>
  `;
}

function renderMatchResult(result: MatchResult): string {
  const winner = result.standings.find((standing) => standing.playerId === result.winnerId);
  const mvp = result.standings.find((standing) => standing.playerId === result.mvpPlayerId);
  const requested = snapshot?.rematchRequestedBy?.length ?? 0;
  const connected = snapshot?.players.filter((player) => player.connected).length ?? result.standings.filter((standing) => standing.connected).length;
  return `
    <div class="result-card">
      <div class="hud-title">Match Result</div>
      <strong>${escapeHtml(result.winningTeamId ? `${result.winningTeamId.toUpperCase()} wins` : `${winner?.name ?? "Captain"} wins`)}</strong>
      ${mvp ? `<div class="muted">MVP ${escapeHtml(mvp.name)} &middot; ${mvp.score} sinks</div>` : ""}
      ${result.standings.map((standing) => `<div class="score-row"><span>${teamBadge(standing.teamId)}${escapeHtml(standing.name)}</span><strong>${standing.score}</strong></div>`).join("")}
      <div class="muted">Rematch ${requested}/${Math.max(2, connected)} ready</div>
    </div>
  `;
}

function showCenterMessage(message: string, durationMs: number): void {
  centerMessage = message;
  centerMessageUntil = performance.now() + durationMs;
}





function tokenKey(code: string): string {
  return `submarine:${code}:token`;
}

function inviteUrlForRoom(code: string): string {
  const url = new URL(window.location.href);
  url.searchParams.set("room", normalizeRoomCode(code));
  return url.toString();
}

function setRoomInUrl(code: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set("room", normalizeRoomCode(code));
  url.searchParams.delete("practice");
  window.history.replaceState({}, "", url);
}

function clearRoomInUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete("room");
  url.searchParams.delete("practice");
  window.history.replaceState({}, "", url);
}





